// client/src/context/auth/AuthContext.js
import React, { createContext, useReducer, useContext, useEffect } from 'react';
import api from '../../utils/apiConfig'; // Import the configured API
import authReducer from './authReducer';
import setAuthToken from '../../utils/setAuthToken';
import {
  REGISTER_SUCCESS,
  REGISTER_FAIL,
  USER_LOADED,
  AUTH_ERROR,
  LOGIN_SUCCESS,
  LOGIN_FAIL,
  LOGOUT,
  CLEAR_ERRORS,
  FORGOT_PASSWORD_SUCCESS,
  FORGOT_PASSWORD_FAIL,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_FAIL,
  UPDATE_PROFILE_SUCCESS,
  UPDATE_PROFILE_FAIL
} from '../types';

// Create context
const AuthContext = createContext();

// Provider component
export const AuthProvider = ({ children }) => {
  const initialState = {
    token: localStorage.getItem('token'),
    isAuthenticated: null,
    loading: true,
    user: null,
    error: null,
    success: null
  };

  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user
  const loadUser = async () => {
    if (localStorage.token) {
      setAuthToken(localStorage.token);
    }

    try {
      // Use the api instance with pre-configured baseURL
      const res = await api.get('/auth/me');

      dispatch({
        type: USER_LOADED,
        payload: res.data.data
      });
    } catch (err) {
      dispatch({ type: AUTH_ERROR });
    }
  };

  // Register user
  const register = async (formData) => {
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      const res = await api.post('/auth/register', formData, config);

      dispatch({
        type: REGISTER_SUCCESS,
        payload: res.data
      });

      loadUser();
    } catch (err) {
      dispatch({
        type: REGISTER_FAIL,
        payload: err.response?.data?.message || 'Registration failed'
      });
    }
  };

  // Login user
  const login = async (formData) => {
    // Development testing account bypass
    if (formData.email === 'admin' && formData.password === 'admin') {
      // Create a mock admin token
      const mockToken = 'admin-test-token-123456';
      localStorage.setItem('token', mockToken);
      
      // Create a mock admin user
      const mockUser = {
        _id: 'admin123',
        firstName: 'Admin',
        lastName: 'User',
        email: 'admin@example.com',
        role: 'admin',
        masonicRank: 'Worshipful Master',
        motherLodge: 'Grand Admin Lodge #123',
        initiationDate: '2015-01-01',
        fcDate: '2015-02-01',
        mmDate: '2015-03-01',
        city: 'Philadelphia',
        state: 'PA',
        profileImage: null
      };
      
      // Set login success first to authenticate
      dispatch({
        type: LOGIN_SUCCESS,
        payload: { token: mockToken }
      });
      
      // Then load the mock user
      dispatch({
        type: USER_LOADED,
        payload: mockUser
      });
      
      return;
    }
    
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      const res = await api.post('/auth/login', formData, config);

      dispatch({
        type: LOGIN_SUCCESS,
        payload: res.data
      });

      loadUser();
    } catch (err) {
      dispatch({
        type: LOGIN_FAIL,
        payload: err.response?.data?.message || 'Invalid credentials'
      });
    }
  };

  // Logout
  const logout = async () => {
    try {
      // Skip API call if using test admin account
      if (localStorage.getItem('token') !== 'admin-test-token-123456') {
        await api.get('/auth/logout');
      }
    } catch (err) {
      console.error('Logout error', err);
    }

    dispatch({ type: LOGOUT });
  };

  // Forgot password
  const forgotPassword = async (email) => {
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      const res = await api.post('/auth/forgot-password', { email }, config);

      dispatch({
        type: FORGOT_PASSWORD_SUCCESS,
        payload: res.data.message
      });
      
      return true;
    } catch (err) {
      dispatch({
        type: FORGOT_PASSWORD_FAIL,
        payload: err.response?.data?.message || 'Failed to send password reset email'
      });
      
      return false;
    }
  };

  // Reset password
  const resetPassword = async (password, token) => {
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      const res = await api.put(`/auth/reset-password/${token}`, { password }, config);

      dispatch({
        type: RESET_PASSWORD_SUCCESS,
        payload: res.data
      });

      return true;
    } catch (err) {
      dispatch({
        type: RESET_PASSWORD_FAIL,
        payload: err.response?.data?.message || 'Failed to reset password'
      });

      return false;
    }
  };

  // Update profile
  const updateProfile = async (formData) => {
    // If using test admin account, just update the user in state without API call
    if (localStorage.getItem('token') === 'admin-test-token-123456') {
      const updatedUser = {
        ...state.user,
        ...formData
      };
      
      dispatch({
        type: UPDATE_PROFILE_SUCCESS,
        payload: updatedUser
      });
      
      return true;
    }
    
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      const res = await api.put('/users/profile', formData, config);

      dispatch({
        type: UPDATE_PROFILE_SUCCESS,
        payload: res.data.data
      });

      return true;
    } catch (err) {
      dispatch({
        type: UPDATE_PROFILE_FAIL,
        payload: err.response?.data?.message || 'Failed to update profile'
      });

      return false;
    }
  };

  // Update password
  const updatePassword = async (passwordData) => {
    // If using test admin account, just return success without API call
    if (localStorage.getItem('token') === 'admin-test-token-123456') {
      return true;
    }
    
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      await api.put('/auth/update-password', passwordData, config);
      
      return true;
    } catch (err) {
      dispatch({
        type: UPDATE_PROFILE_FAIL,
        payload: err.response?.data?.message || 'Failed to update password'
      });

      return false;
    }
  };

  // Clear errors
  const clearErrors = () => dispatch({ type: CLEAR_ERRORS });

  useEffect(() => {
    if (localStorage.token) {
      loadUser();
    } else {
      dispatch({ type: AUTH_ERROR });
    }
    // eslint-disable-next-line
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        loading: state.loading,
        user: state.user,
        error: state.error,
        success: state.success,
        register,
        login,
        logout,
        loadUser,
        clearErrors,
        forgotPassword,
        resetPassword,
        updateProfile,
        updatePassword
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => useContext(AuthContext);

export default AuthContext;